import React from 'react';
import PropTypes from 'prop-types';
import { DropdownToggle, UncontrolledDropdown, DropdownMenu } from 'reactstrap';
import { kebabCase, startCase, camelCase } from 'lodash';
import Icon from '../common/containers/icon';
import * as paths from '../../utils/paths';
import entityComposition from '../common/classes/entityComposition';
import MenuDropdownItem from './menuDropdownItem';
import { checkPermission, makeResource } from '../../../behaviors/src/lib/permissions';
import { getUser } from '../common/classes/permissions';
const AdminMenu = _ref => {
  var _permissions$general, _permissions$general2, _permissions$summarie, _permissions$general3, _permissions$general4, _permissions$general5;
  let _ref$adminCabinets = _ref.adminCabinets,
    adminCabinets = _ref$adminCabinets === void 0 ? [] : _ref$adminCabinets,
    _ref$hasSupersetToken = _ref.hasSupersetToken,
    hasSupersetToken = _ref$hasSupersetToken === void 0 ? false : _ref$hasSupersetToken;
  const hasAdminSectionPermission = paths.hasAdminSectionPermission,
    getPermissions = paths.getPermissions;
  const permissions = getPermissions();
  const user = getUser();
  const jobOrUserEdit = ((_permissions$general = permissions.general) === null || _permissions$general === void 0 ? void 0 : _permissions$general.includes('job_edit')) || ((_permissions$general2 = permissions.general) === null || _permissions$general2 === void 0 ? void 0 : _permissions$general2.includes('user_edit'));
  const checkAdminPermission = section => jobOrUserEdit && hasAdminSectionPermission(section);
  const hardCodedMenuItems = {
    jobs: {
      identifier: 'jobs',
      sortOrder: 0,
      isPermittedToViewMenu: checkAdminPermission('jobs'),
      glyph: 'list'
    },
    employees: {
      identifier: 'employees',
      sortOrder: 1,
      isPermittedToViewMenu: checkAdminPermission('employees'),
      glyph: 'user'
    },
    summaries: {
      identifier: 'summaries',
      sortOrder: 2,
      isPermittedToViewMenu: ((_permissions$summarie = permissions.summaries) === null || _permissions$summarie === void 0 ? void 0 : _permissions$summarie.length) && checkAdminPermission('summaries'),
      glyph: 'download'
    },
    organizations: {
      identifier: 'organizations',
      sortOrder: 3,
      isPermittedToViewMenu: checkAdminPermission('organizations'),
      glyph: 'home'
    },
    pickerMenu: {
      identifier: 'pickers',
      sortOrder: 4,
      isPermittedToViewMenu: (_permissions$general3 = permissions.general) === null || _permissions$general3 === void 0 ? void 0 : _permissions$general3.includes('picker_edit'),
      glyph: 'sticky-note'
    },
    contractSpec: {
      identifier: 'contractSpecs',
      sortOrder: 5,
      isPermittedToViewMenu: checkAdminPermission('contractSpec'),
      glyph: 'list-alt'
    },
    reportTags: {
      identifier: 'reportTags',
      sortOrder: 6,
      isPermittedToViewMenu: checkAdminPermission('reportTags'),
      glyph: 'tags'
    },
    dashboard: {
      identifier: 'dashboard',
      sortOrder: 7,
      isPermittedToViewMenu: (_permissions$general4 = permissions.general) === null || _permissions$general4 === void 0 ? void 0 : _permissions$general4.includes('dashboard')
    },
    settings: {
      identifier: 'settings',
      sortOrder: 8,
      isPermittedToViewMenu: false // not yet implemented on web
    },
    userGuide: {
      identifier: 'userGuide',
      sortOrder: 9,
      isPermittedToViewMenu: false // not yet implemented on web
    },
    design: {
      identifier: 'design',
      sortOrder: 10,
      isPermittedToViewMenu: false // not yet implemented on web
    },
    appendix8: {
      identifier: 'appendix8',
      sortOrder: 11,
      isPermittedToViewMenu: false // not yet implemented on web
    },
    whatsNew: {
      identifier: 'whatsNew',
      sortOrder: 12,
      isPermittedToViewMenu: false // not yet implemented on web
    },
    announcements: {
      identifier: 'announcements',
      sortOrder: 13,
      isPermittedToViewMenu: false // not yet implemented on web
    }
  };
  const validAdminMenuOptions = [];

  // Add permitted hardcoded admin sections
  Object.keys(hardCodedMenuItems).forEach(section => {
    if (permissions.adminSections.includes(section)) {
      validAdminMenuOptions.push(hardCodedMenuItems[section]);
    }
  });

  // The picker edit is intended to exist under the admin menu options
  // but the permission for picker edit lives under permissions.general
  // section so it must be checked as well and included in validAdminMenuOptions if present.
  if ((_permissions$general5 = permissions.general) !== null && _permissions$general5 !== void 0 && _permissions$general5.includes('picker_edit')) {
    validAdminMenuOptions.push(hardCodedMenuItems.pickerMenu);
  }

  // configurable cabinets with tabGroup.identifer = 'admin'
  const configurableCabinetIdentifiers = [];
  adminCabinets.forEach(adminCabinet => {
    const identifier = adminCabinet.identifier,
      title = adminCabinet.title,
      _adminCabinet$formTyp = adminCabinet.formTypeIdentifiers,
      formTypeIdentifiers = _adminCabinet$formTyp === void 0 ? [] : _adminCabinet$formTyp,
      _adminCabinet$client$ = adminCabinet.client.web,
      _adminCabinet$client$2 = _adminCabinet$client$.layout,
      _adminCabinet$client$3 = _adminCabinet$client$2 === void 0 ? {} : _adminCabinet$client$2,
      superset = _adminCabinet$client$3.superset,
      _adminCabinet$client$4 = _adminCabinet$client$.icon,
      glyph = _adminCabinet$client$4 === void 0 ? 'folder-open' : _adminCabinet$client$4,
      _adminCabinet$client$5 = _adminCabinet$client$.tabGroup,
      _adminCabinet$client$6 = _adminCabinet$client$5 === void 0 ? {} : _adminCabinet$client$5,
      _adminCabinet$client$7 = _adminCabinet$client$6.sortOrder,
      sortOrder = _adminCabinet$client$7 === void 0 ? validAdminMenuOptions.length : _adminCabinet$client$7;
    const isPermittedToViewMenu = formTypeIdentifiers.some(ft => checkPermission(makeResource('form', ft), 'view_list', permissions, user));
    validAdminMenuOptions.push({
      glyph,
      identifier,
      title,
      isPermittedToViewMenu: superset ? hasSupersetToken : isPermittedToViewMenu,
      path: "/#/cabinets/".concat(identifier),
      sortOrder
    });
    configurableCabinetIdentifiers.push(identifier);
  });

  // Sorts, checks specific permissions and renders the item component
  const adminOptions = validAdminMenuOptions.sort((a, b) => a.sortOrder - b.sortOrder).filter(_ref2 => {
    let isPermittedToViewMenu = _ref2.isPermittedToViewMenu;
    return isPermittedToViewMenu;
  }).map(_ref3 => {
    let _ref3$glyph = _ref3.glyph,
      glyph = _ref3$glyph === void 0 ? 'chart-bar' : _ref3$glyph,
      identifier = _ref3.identifier,
      _ref3$path = _ref3.path,
      path = _ref3$path === void 0 ? "/#/admin/".concat(identifier) : _ref3$path,
      _ref3$title = _ref3.title,
      title = _ref3$title === void 0 ? startCase(camelCase(identifier)) : _ref3$title;
    return /*#__PURE__*/React.createElement(MenuDropdownItem, {
      key: identifier,
      className: "t-h-nav-admin-".concat(kebabCase(identifier)),
      title: title,
      glyph: glyph,
      href: path
    });
  });
  return adminOptions.length > 0 ? /*#__PURE__*/React.createElement(UncontrolledDropdown, {
    nav: true,
    innavbar: "true",
    className: "t-h-nav-admin-dropdown",
    key: "dropdownMenu",
    id: "adminDropdownMenu",
    style: {
      textAlign: 'center'
    }
  }, /*#__PURE__*/React.createElement(DropdownToggle, {
    nav: true,
    caret: true
  }, /*#__PURE__*/React.createElement(Icon, {
    glyph: "cog"
  }), /*#__PURE__*/React.createElement("br", null), "Admin"), /*#__PURE__*/React.createElement(DropdownMenu, null, adminOptions)) : /*#__PURE__*/React.createElement("div", null);
};
AdminMenu.propTypes = {
  adminCabinets: PropTypes.arrayOf(PropTypes.shape({})),
  hasSupersetToken: PropTypes.bool
};
export { AdminMenu as TestAdminMenu };
export default entityComposition(AdminMenu, ['settings'], {
  adminCabinets: state => {
    var _state$settings;
    return state === null || state === void 0 || (_state$settings = state.settings) === null || _state$settings === void 0 || (_state$settings = _state$settings.settings) === null || _state$settings === void 0 || (_state$settings = _state$settings.cabinetDefinitions) === null || _state$settings === void 0 ? void 0 : _state$settings.filter(cd => {
      var _cd$client;
      return (cd === null || cd === void 0 || (_cd$client = cd.client) === null || _cd$client === void 0 || (_cd$client = _cd$client.web) === null || _cd$client === void 0 || (_cd$client = _cd$client.tabGroup) === null || _cd$client === void 0 ? void 0 : _cd$client.identifier) === 'admin';
    });
  },
  hasSupersetToken: state => {
    var _state$login;
    return (state === null || state === void 0 || (_state$login = state.login) === null || _state$login === void 0 || (_state$login = _state$login.user) === null || _state$login === void 0 || (_state$login = _state$login.superset) === null || _state$login === void 0 || (_state$login = _state$login.dashboards) === null || _state$login === void 0 ? void 0 : _state$login.length) > 0;
  }
}, []);